import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getTermLoanRepaymentSplitByLoadId } from "../../actions/loan.action";
import Modal from "../../components/modal";
import Table, { TableColumns } from "../../components/table";
import { TermLoanRepaymentSplitDetails } from "../../dto";
import dispatch from "../../middleware";
import { ReduxState } from "../../reducers";
import { dateToDDMMYYYY, isLoadingActive } from "../../utils";

interface TermLoanRepaymentProps {
  loanId: string;
}

const COLUMNS_FOR_TERM_LOAN_REPAYMENT_SPLIT: TableColumns[] = [
  {
    label: "Payment Date",
    field: "paidAt",
    style: { textAlign: "center" },
  },
  { label: "Amount", field: "amount", style: { textAlign: "center" } },
  {
    label: "Principal Paid",
    field: "totalPrincipalPaid",
    style: { textAlign: "center" },
  },
  {
    label: "Interest Paid",
    field: "totalInterestPaid",
    style: { textAlign: "center" },
  },
  {
    label: "Closing Balance",
    field: "closingBalance",
    style: { textAlign: "center" },
  },
  { label: "Reported On", field: "reportedAt", style: { textAlign: "center" } },
];

function getTableDataForTermLoanRepaymentSplit(
  termLoanRepaymentSplit: TermLoanRepaymentSplitDetails[] | null
) {
  if (!termLoanRepaymentSplit) return [];

  return termLoanRepaymentSplit.map(
    ({
      paidAt,
      reportedAt,
      amount,
      totalInterestPaid,
      totalPrincipalPaid,
      closingBalance,
    }) => ({
      paidAt: dateToDDMMYYYY(paidAt),
      reportedAt: dateToDDMMYYYY(reportedAt),
      amount: amount.toFixed(2),
      totalInterestPaid,
      totalPrincipalPaid: totalPrincipalPaid.toFixed(2),
      closingBalance: closingBalance.toFixed(2),
    })
  );
}

export default function TermLoanRepayment({ loanId }: TermLoanRepaymentProps) {
  const storeDispatch = useDispatch();
  const loadingQueue = useSelector(
    (state: ReduxState) => state.loan.loadingQueue
  );
  const termLoanRepaymentSplit: TermLoanRepaymentSplitDetails[] | null =
    useSelector((state: ReduxState) => state.loan.termLoanRepaymentSplit);
  const isLoading = isLoadingActive(loadingQueue);
  const [isErrorModalActive, setIsErrorModalActive] = useState<{
    active: boolean;
    title: string;
    message: string;
  }>({ active: false, title: "", message: "" });

  async function fetchTermLoanRepaymentSplitByLoanId(loanId: string) {
    if (!loanId) return;
    try {
      await dispatch(storeDispatch, getTermLoanRepaymentSplitByLoadId(loanId));
    } catch (error: any) {
      setIsErrorModalActive({
        active: true,
        title: "Repayments",
        message: error?.message ?? "Unable to load repayments",
      });
    }
  }

  const tableDataForTermLoanRepaymentSplit =
    getTableDataForTermLoanRepaymentSplit(termLoanRepaymentSplit);

  useEffect(() => {
    fetchTermLoanRepaymentSplitByLoanId(loanId);
  }, [loanId]);

  return (
    <section>
      <h3 className="user-page-title mb-4">Payment History</h3>
      <Table
        loading={isLoading}
        columns={COLUMNS_FOR_TERM_LOAN_REPAYMENT_SPLIT}
        data={tableDataForTermLoanRepaymentSplit}
      />
      <Modal
        open={isErrorModalActive.active}
        onClose={() =>
          setIsErrorModalActive((prevState) => ({
            ...prevState,
            active: false,
          }))
        }
        hasCancelButton={false}
        hasSubmitButton={false}
        children={<h4>{isErrorModalActive.message}</h4>}
      />
    </section>
  );
}
